/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { TreeNodeData } from "baseui/tree-view";
import { Path, PathEntry, PathType } from "../../Api/DataTransformation";
import { NodeStructureType, PROTO_STRUCTURES } from "../../Utils/Common";

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Metadata stamped onto leaf HeTreeNodes so the tree can be round-tripped
 * back to Path[][] after a drag-reorder.
 */
export interface PathMeta {
  nodeDescriptorID: string;
  pathType: PathType;
  trimmedPrefix: PathEntry[]; // PathEntries stripped by buildComposerTree, prepended on deserialize
  groupIndex: number; // Which Path[] group this leaf originally belonged to
}

export interface HeTreeNode {
  id: string | number;
  name: string;
  children?: HeTreeNode[];
  _original?: {
    labelText: string;
    label: () => JSX.Element;
  };
  _isField?: boolean;
  /**
   * Present only on leaf nodes. Multiple entries arise when mergeTreeNodes
   * collapses leaves with identical labelText — all metas are accumulated so
   * every original Path can be reconstructed after a reorder.
   */
  _pathMetas?: PathMeta[];
}

// ─── Node Classification ──────────────────────────────────────────────────────

export const getNodeType = (name: string): NodeStructureType => {
  const normalized = name.toLowerCase();

  for (const structure of PROTO_STRUCTURES) {
    if (normalized.includes(structure)) return structure;
  }

  return "field";
};

// ─── Tree Conversion ──────────────────────────────────────────────────────────

/**
 * Converts TreeNodeData (baseui format) to HeTreeNode format,
 * stamping _isField at conversion time so it never changes during drag operations.
 */
export const convertToHeTree = (treeNodes: TreeNodeData[]): HeTreeNode[] => {
  return treeNodes.map((treeNode) => {
    const hasChildren = treeNode.children && treeNode.children.length > 0;
    return {
      id: treeNode.id,
      name: treeNode.labelText || "",
      children: hasChildren ? convertToHeTree(treeNode.children) : undefined,
      _original: {
        labelText: treeNode.labelText || "",
        label: treeNode.label as () => JSX.Element,
      },
      _isField: !hasChildren,
      _pathMetas: (treeNode as any)._pathMetas as PathMeta[] | undefined,
    };
  });
};

/**
 * Builds a flat map of nodeId -> _isField from the full tree.
 * Should be called on the PRE-DROP treeData for stable field identification.
 */
export const buildIsFieldMap = (
  nodes: HeTreeNode[]
): Map<string | number, boolean> => {
  const map = new Map<string | number, boolean>();
  const collect = (treeNodes: HeTreeNode[]): void => {
    treeNodes.forEach((treeNode) => {
      map.set(treeNode.id, treeNode._isField ?? false);
      if (treeNode.children) collect(treeNode.children);
    });
  };
  collect(nodes);
  return map;
};

// ─── Tree Search ─────────────────────────────────────────────────────────────

export const findNodeById = (
  treeNodes: HeTreeNode[],
  id: string | number
): HeTreeNode | null => {
  for (const treeNode of treeNodes) {
    if (treeNode.id === id) return treeNode;
    if (treeNode.children) {
      const found = findNodeById(treeNode.children, id);
      if (found) return found;
    }
  }
  return null;
};

export const getAllNodeIds = (treeNodes: HeTreeNode[]): (string | number)[] => {
  const ids: (string | number)[] = [];
  treeNodes.forEach((treeNode) => {
    ids.push(treeNode.id);
    if (treeNode.children) ids.push(...getAllNodeIds(treeNode.children));
  });
  return ids;
};

// ─── Locked Structure Validation ─────────────────────────────────────────────

/**
 * Snapshots the exact children IDs (in order) for all oneof/enum/service nodes
 * AND all their descendants. This ensures the entire locked structure is immutable.
 * Call this on the PRE-DROP tree, then compare against the post-drop tree.
 */
export interface LockedSnapshot {
  childIds: (string | number)[];
  orderFree: boolean; // If true, children can be reordered within structures, but cannot add or remove fields
}

export const buildLockedStructureSnapshot = (
  nodes: HeTreeNode[]
): Map<string | number, LockedSnapshot> => {
  const map = new Map<string | number, LockedSnapshot>();

  const collect = (
    treeNodes: HeTreeNode[],
    insideLocked: boolean = false,
    insideService: boolean = false
  ): void => {
    treeNodes.forEach((treeNode) => {
      const nodeType = getNodeType(treeNode.name);
      const isService = nodeType === "service";
      const isLocked = nodeType === "oneof" || nodeType === "enum" || isService;

      // If this is a locked structure OR we're inside one, snapshot its children
      if (isLocked || insideLocked) {
        map.set(treeNode.id, {
          childIds: (treeNode.children ?? []).map((c) => c.id),
          // Direct children of a service node may be reordered freely.
          // Descendants deeper inside (e.g. inside an rpc block) stay fully locked.
          orderFree: isService,
        });
      }

      if (treeNode.children) {
        collect(
          treeNode.children,
          isLocked || insideLocked,
          isService || insideService
        );
      }
    });
  };

  collect(nodes);
  return map;
};

/**
 * Verifies that all locked structures (oneof/enum/service) AND their descendants
 * still have exactly the same children in the same order after a drop.
 * This prevents both adding/removing from locked structures AND adding items
 * under fields inside those structures.
 */
export const validateLockedStructures = (
  treeNodes: HeTreeNode[],
  snapshot: Map<string | number, LockedSnapshot>
): boolean => {
  for (const treeNode of treeNodes) {
    if (snapshot.has(treeNode.id)) {
      const { childIds: expectedChildren, orderFree } = snapshot.get(
        treeNode.id
      );
      const actualChildren = (treeNode.children ?? []).map((c) => c.id);

      // Always enforce count — no adding or removing
      if (actualChildren.length !== expectedChildren.length) return false;

      if (!orderFree) {
        // Strict: same count and same order (oneof, enum, rpc internals)
        for (let i = 0; i < actualChildren.length; i++) {
          if (actualChildren[i] !== expectedChildren[i]) return false;
        }
      } else {
        // Order-free: same count and same set of IDs, any order (service children)
        const expectedSet = new Set(expectedChildren);
        for (const id of actualChildren) {
          if (!expectedSet.has(id)) return false;
        }
      }
    }

    if (treeNode.children) {
      if (!validateLockedStructures(treeNode.children, snapshot)) return false;
    }
  }
  return true;
};

// ─── Drop Validation ─────────────────────────────────────────────────────────

/**
 * Returns true if any service node exists at depth > 0 (i.e. nested inside anything).
 */
export const hasNestedService = (treeNodes: HeTreeNode[]): boolean => {
  const check = (nodes: HeTreeNode[], depth: number): boolean => {
    return nodes.some((node) => {
      if (depth > 0 && getNodeType(node.name) === "service") return true;
      if (getNodeType(node.name) === "service") return false;
      return node.children ? check(node.children, depth + 1) : false;
    });
  };
  return check(treeNodes, 0);
};

/**
 * Fixes invalid nesting after a drop:
 * - Root level: only messages and services are allowed
 * - Anything dropped inside a field is hoisted up to sibling level
 *
 * Returns { fixed, hoisted } where hoisted items have been placed after
 * their incorrect parent in `fixed`. An empty `fixed` array signals
 * an unrecoverable drop that should be rejected entirely.
 */
export const fixInvalidNesting = (
  treeNodes: HeTreeNode[],
  isFieldMap: Map<string | number, boolean>,
  parentIsField: boolean = false,
  depth: number = 0
): { fixed: HeTreeNode[]; hoisted: HeTreeNode[] } => {
  const fixed: HeTreeNode[] = [];
  const hoisted: HeTreeNode[] = [];

  for (const treeNode of treeNodes) {
    const isField = isFieldMap.get(treeNode.id) ?? false;
    const treeNodeType = getNodeType(treeNode.name);

    if (depth === 0) {
      if (treeNodeType !== "message" && treeNodeType !== "service") {
        // Unrecoverable — a non-message, non-service node ended up at root.
        return { fixed: [], hoisted: [] };
      }
      // Valid root node — recurse into its children.
      if (treeNode.children && treeNode.children.length > 0) {
        const { fixed: fixedChildren, hoisted: hoistedFromChildren } =
          fixInvalidNesting(treeNode.children, isFieldMap, isField, depth + 1);
        fixed.push({
          ...treeNode,
          children: fixedChildren.length > 0 ? fixedChildren : undefined,
        });
        fixed.push(...hoistedFromChildren);
      } else {
        fixed.push(treeNode);
      }
    } else if (parentIsField) {
      // Depth > 0: anything dropped into a field gets hoisted to sibling level.
      hoisted.push(treeNode);
    } else if (treeNode.children && treeNode.children.length > 0) {
      // Normal case: recurse into children.
      const { fixed: fixedChildren, hoisted: hoistedFromChildren } =
        fixInvalidNesting(treeNode.children, isFieldMap, isField, depth + 1);

      fixed.push({
        ...treeNode,
        children: fixedChildren.length > 0 ? fixedChildren : undefined,
      });

      // Hoisted nodes go right after their incorrect parent as siblings.
      fixed.push(...hoistedFromChildren);
    } else {
      fixed.push(treeNode);
    }
  }

  return { fixed, hoisted };
};
// ─── Tree Builder ─────────────────────────────────────────────────────────────

/**
 * Builds a TreeNodeData tree from flat path entry arrays.
 * trimDepth removes the first N entries (shared prefix). pathMetas is parallel
 * to paths — each entry carries leaf metadata for round-tripping back to Path[][].
 */
export function buildTreeForPaths(
  paths: Path[],
  trimDepth: number,
  syntaxHighlighter: (text: string) => JSX.Element,
  idPrefix: string = "",
  pathMetas?: PathMeta[]
): TreeNodeData[] {
  const rootNodes: TreeNodeData[] = [];

  paths.forEach((path: Path, pathIndex: number) => {
    const pathEntries = path.pathEntries || [];
    const trimmedEntries = pathEntries.slice(trimDepth);
    let currentLevel = rootNodes;

    trimmedEntries.forEach((pathEntry: PathEntry, entryIndex: number) => {
      const label = pathEntry.byString || "";
      const isLeaf = entryIndex === trimmedEntries.length - 1;
      if (!label) return;

      let existingNode = currentLevel.find(
        (node) => node.labelText === label
      ) as (TreeNodeData & { _pathMetas?: PathMeta[] }) | undefined;

      if (!existingNode) {
        const newNode: TreeNodeData & { _pathMetas?: PathMeta[] } = {
          id: `${idPrefix}-path${pathIndex}-entry${entryIndex}`,
          labelText: label,
          label: () => syntaxHighlighter(label),
          isExpanded: true,
          children: [],
        };

        if (isLeaf && pathMetas?.[pathIndex]) {
          newNode._pathMetas = [pathMetas[pathIndex]];
        }

        currentLevel.push(newNode);
        existingNode = newNode;
      } else if (isLeaf && pathMetas?.[pathIndex]) {
        // Leaf label already exists — accumulate meta so the path isn't lost on deserialize
        if (!existingNode._pathMetas) existingNode._pathMetas = [];
        existingNode._pathMetas.push(pathMetas[pathIndex]);
      }
      if (!isLeaf) {
        currentLevel = existingNode.children;
      }
    });
  });

  return rootNodes;
}

/** Computes how many leading path entries are shared across all paths in a group. */
function computeEffectiveTrimDepth(groupPaths: Path[]): number {
  const allPathStrings = groupPaths
    .filter((path) => path.pathEntries && path.pathEntries.length > 0)
    .map((path) => path.pathEntries.map((entry: PathEntry) => entry.byString));

  if (allPathStrings.length === 0) return 0;

  const shortestPathLength = Math.min(
    ...allPathStrings.map((entries) => entries.length)
  );

  // Count how many leading entries are identical across ALL paths in the group
  let sharedPrefixDepth = 0;
  for (let i = 0; i < shortestPathLength; i++) {
    const val = allPathStrings[0][i];
    if (allPathStrings.every((entries) => entries[i] === val)) {
      sharedPrefixDepth++;
    } else {
      break;
    }
  }

  // Trim up to (but not including) the last shared entry so it becomes the tree root.
  // Cap at shortestPathLength - 2 to always preserve at least one structural node
  // above the leaf fields (handles single-path groups correctly).
  const maxTrimToKeepLeafContext = shortestPathLength - 2;
  const trimToLastSharedAncestor = sharedPrefixDepth - 1;
  return Math.max(
    0,
    Math.min(trimToLastSharedAncestor, maxTrimToKeepLeafContext)
  );
}

/** Merges incoming nodes into existing ones by labelText, accumulating leaf metas on collision. */
function mergeTreeNodes(
  existingTreeData: (TreeNodeData & { _pathMetas?: PathMeta[] })[],
  incomingTreeData: (TreeNodeData & { _pathMetas?: PathMeta[] })[]
): void {
  incomingTreeData.forEach((incomingTreeNode) => {
    const match = existingTreeData.find(
      (existingNode) => existingNode.labelText === incomingTreeNode.labelText
    ) as (TreeNodeData & { _pathMetas?: PathMeta[] }) | undefined;

    if (match) {
      if (incomingTreeNode.children && incomingTreeNode.children.length > 0) {
        if (!match.children) match.children = [];
        mergeTreeNodes(match.children, incomingTreeNode.children);
      } else if (incomingTreeNode._pathMetas?.length) {
        if (!match._pathMetas) match._pathMetas = [];
        match._pathMetas.push(...incomingTreeNode._pathMetas);
      }
    } else {
      existingTreeData.push(incomingTreeNode);
    }
  });
}

// ─── Leaf identity ─────────────────────────────────────────────────────────

/**
 * A stable identity for a single leaf Path, independent of trimming/grouping.
 * Two Paths across different composerData groups share this key iff they
 * reference the exact same field in the source data.
 */
function leafIdentityKey(path: Path): string {
  const entries = (path.pathEntries || []).map((e) => e.byString).join("/");
  return `${path.nodeDescriptorID}::${path.pathType}::${entries}`;
}

/**
 * Builds a complete composer tree from composerData paths.
 * Analyzes path structure to determine full messages vs granular selections,
 * and constructs the appropriate tree hierarchy.
 */
export function buildComposerTree(
  composerData: Path[][],
  syntaxHighlighter: (text: string) => JSX.Element
): TreeNodeData[] {
  if (!composerData || composerData.length === 0) return [];

  const allRootNodes: (TreeNodeData & { _pathMetas?: PathMeta[] })[] = [];
  const seenLeafIdentities = new Set<string>();

  // Track single root for rendundant fields
  const redundantRootsByLabel = new Map<
    string,
    TreeNodeData & { _pathMetas?: PathMeta[] }
  >();

  composerData.forEach((pathGroup: Path[], groupIndex: number) => {
    const groupPaths: Path[] =
      Array.isArray(pathGroup) &&
      pathGroup.length > 0 &&
      (pathGroup[0] as any)?.pathEntries
        ? pathGroup
        : [pathGroup as unknown as Path];

    if (!groupPaths || groupPaths.length === 0) return;

    const groupLeafIdentities = groupPaths.map(leafIdentityKey);
    const isRedundantGroup = groupLeafIdentities.some((key) =>
      seenLeafIdentities.has(key)
    );

    const trimDepth = computeEffectiveTrimDepth(groupPaths);

    const pathMetas: PathMeta[] = groupPaths.map((path) => ({
      nodeDescriptorID: path.nodeDescriptorID,
      pathType: path.pathType,
      trimmedPrefix: path.pathEntries.slice(0, trimDepth),
      groupIndex,
    }));

    const subtree = buildTreeForPaths(
      groupPaths,
      trimDepth,
      syntaxHighlighter,
      `group${groupIndex}`,
      pathMetas
    );

    if (isRedundantGroup) {
      subtree.forEach((topLevelNode) => {
        const existingDuplicateRoot = redundantRootsByLabel.get(
          topLevelNode.labelText
        );
        if (existingDuplicateRoot) {
          // Same label already has a duplicate root from an earlier
          // redundant selection — merge into it rather than spawning
          // another separate top-level tree.
          mergeTreeNodes(
            [existingDuplicateRoot],
            [topLevelNode as TreeNodeData & { _pathMetas?: PathMeta[] }]
          );
        } else {
          redundantRootsByLabel.set(
            topLevelNode.labelText,
            topLevelNode as TreeNodeData & { _pathMetas?: PathMeta[] }
          );
          allRootNodes.push(
            topLevelNode as TreeNodeData & { _pathMetas?: PathMeta[] }
          );
        }
      });
    } else {
      mergeTreeNodes(allRootNodes, subtree);
    }

    groupLeafIdentities.forEach((key) => seenLeafIdentities.add(key));
  });

  return allRootNodes;
}

// ─── Duplicate Field Validation ───────────────────────────────────────────

/**
 * Capture any duplicate field names
 */
export const hasDuplicateFieldNames = (treeNodes: HeTreeNode[]): boolean => {
  const hasDuplicatesInList = (nodes: HeTreeNode[]): boolean => {
    const seenNames = new Set<string>();
    for (const node of nodes) {
      if (seenNames.has(node.name)) return true;
      seenNames.add(node.name);
    }
    return false;
  };

  const walk = (nodes: HeTreeNode[]): boolean => {
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        if (hasDuplicatesInList(node.children)) return true;
        if (walk(node.children)) return true;
      }
    }
    return false;
  };

  return walk(treeNodes);
};

// ─── Empty Message Validation ─────────────────────────────────────────────────

/**
 * Builds a set of node IDs that have children in the given tree.
 * Used as the pre-drop snapshot of which nodes are structural (non-empty).
 */
export const buildHasChildrenSet = (
  heTreeNodes: HeTreeNode[]
): Set<string | number> => {
  const treeSet = new Set<string | number>();
  const collect = (treeNodes: HeTreeNode[]): void => {
    treeNodes.forEach((treeNode) => {
      if (treeNode.children && treeNode.children.length > 0) {
        treeSet.add(treeNode.id);
        collect(treeNode.children);
      }
    });
  };
  collect(heTreeNodes);
  return treeSet;
};

/**
 * Returns true if any node that previously had children (per hasChildrenSet)
 * now has no children after a drop. Prevents messages from being emptied out.
 */
export const hasEmptiedMessage = (
  heTreeNodes: HeTreeNode[],
  hasChildrenSet: Set<string | number>
): boolean => {
  const currentChildCounts = new Map<string | number, number>();
  const collect = (nodes: HeTreeNode[]): void => {
    nodes.forEach((node) => {
      currentChildCounts.set(node.id, node.children?.length ?? 0);
      if (node.children) collect(node.children);
    });
  };
  collect(heTreeNodes);

  for (const id of hasChildrenSet) {
    const currentCount = currentChildCounts.get(id); // undefined = node vanished entirely
    if (currentCount === undefined || currentCount === 0) return true;
  }
  return false;
};

// ─── Path Deserialization ─────────────────────────────────────────────────────

/** Walks the tree and collects every leaf with its full ancestor label chain. */
function collectLeaves(
  treeNodes: HeTreeNode[],
  ancestorEntries: PathEntry[] = []
): { leaf: HeTreeNode; ancestorEntries: PathEntry[] }[] {
  const results: { leaf: HeTreeNode; ancestorEntries: PathEntry[] }[] = [];

  for (const treeNode of treeNodes) {
    const currentEntry: PathEntry = { byString: treeNode.name };

    if (!treeNode.children || treeNode.children.length === 0) {
      results.push({
        leaf: treeNode,
        ancestorEntries: [...ancestorEntries, currentEntry],
      });
    } else {
      results.push(
        ...collectLeaves(treeNode.children, [...ancestorEntries, currentEntry])
      );
    }
  }

  return results;
}

/**
 * Converts a (possibly reordered) HeTreeNode[] back to Path[][].
 *
 * Grouping strategy: ONE GROUP PER TOP-LEVEL TREE NODE, emitted in visual
 * (top-to-bottom) order. This is deliberately NOT keyed off the original
 * meta.groupIndex.
 *
 * Why: meta.groupIndex records the *original selection grouping* — which Path[]
 * a leaf was first added in. After a drag-reorder that grouping no longer maps to
 * the on-screen order. Bucketing by groupIndex (and sorting by it) would round-trip
 * the saved Path[][] back to the ORIGINAL top-level order on reload — e.g. a
 * `service` dragged to the top would snap back to the bottom, and a field moved
 * between messages (like `bool ok`) could be pulled back into its original parent.
 *
 * Grouping by the current top-level node instead guarantees:
 *  - Top-level order is preserved (groups are emitted in tree order).
 *  - Each structure's children stay together in one group, so buildComposerTree
 *    reconstructs them in place without relying on cross-group label merging
 *    (which is what reordered a moved leaf back to its origin).
 *
 * The reconstructed Path objects themselves are unchanged (same nodeDescriptorID,
 * pathType, and full pathEntries) — only their bucketing and order change, so the
 * saved data format the project loader consumes is identical.
 *
 * Each leaf may carry multiple _pathMetas (from label-collision merges); every
 * meta is reconstructed. Leaves without _pathMetas are skipped.
 */
export function deserializeTreeToPaths(tree: HeTreeNode[]): Path[][] {
  const groups: Path[][] = [];

  for (const rootNode of tree) {
    const leaves = collectLeaves([rootNode]);

    const groupPaths: Path[] = leaves
      .filter(({ leaf }) => leaf._pathMetas?.length)
      .flatMap(({ leaf, ancestorEntries }) =>
        leaf._pathMetas.map((meta) => ({
          nodeDescriptorID: meta.nodeDescriptorID,
          pathType: meta.pathType,
          pathEntries: [...meta.trimmedPrefix, ...ancestorEntries],
        }))
      );
    // Skip purely structural top-level nodes that carry no path-bearing leaves.
    if (groupPaths.length > 0) groups.push(groupPaths);
  }

  return groups;
}
